import {
  adjustStockSchema,
  createItemSchema,
  issueStockSchema,
  receiveStockSchema,
} from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { Notice, Pager, errorMessage, primaryButton, selectClass } from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import { formatMinor, parseMajor } from '../../lib/money';
import { withCurrency, type Item, type Message, type Movement, type Supplier } from './shared';

type Errors = Record<string, string>;
type Check = { success: boolean; error?: { issues: { path: PropertyKey[]; message: string }[] } };

const blankItem = { sku: '', name: '', unit: '', category: '', reorder: '' };
const blankReceive = { itemId: '', supplierId: '', quantity: '', cost: '', reference: '' };
const blankIssue = { itemId: '', quantity: '', issuedTo: '' };
const blankAdjust = { itemId: '', quantity: '', cost: '', note: '' };

export function StockTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [lowOnly, setLowOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [itemForm, setItemForm] = useState(blankItem);
  const [receiveForm, setReceiveForm] = useState(blankReceive);
  const [issueForm, setIssueForm] = useState(blankIssue);
  const [adjustForm, setAdjustForm] = useState(blankAdjust);
  const [errors, setErrors] = useState<Errors>({});
  const [message, setMessage] = useState<Message>(null);

  const items = useQuery({
    queryKey: ['inventory', schoolId, 'items', q, lowOnly, page],
    queryFn: () =>
      apiList<Item>(`/schools/${schoolId}/inventory/items`, {
        query: { q, lowOnly: lowOnly ? 'true' : undefined, page, pageSize: 20 },
      }).then(withCurrency),
  });
  const allItems = useQuery({
    queryKey: ['inventory', schoolId, 'items', 'all'],
    queryFn: () =>
      apiList<Item>(`/schools/${schoolId}/inventory/items`, {
        query: { status: 'active', pageSize: 100 },
      }).then((r) => r.data),
  });
  const suppliers = useQuery({
    queryKey: ['inventory', schoolId, 'suppliers', 'all'],
    queryFn: () =>
      apiList<Supplier>(`/schools/${schoolId}/inventory/suppliers`, {
        query: { status: 'active', pageSize: 100 },
      }).then((r) => r.data),
    enabled: can('inventory.receive', schoolId),
  });
  const movements = useQuery({
    queryKey: ['inventory', schoolId, 'movements'],
    queryFn: () =>
      apiList<Movement>(`/schools/${schoolId}/inventory/stock/movements`, {
        query: { pageSize: 10 },
      }),
  });
  const currency = items.data?.currency ?? 'USD';

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['inventory', schoolId] });
  const useSend = (path: string, done: () => void, ok: MessageKey) =>
    useMutation({
      mutationFn: (body: object) =>
        api(`/schools/${schoolId}/inventory/${path}`, { method: 'POST', body }),
      onSuccess: () => {
        done();
        setMessage({ kind: 'ok', text: t(ok) });
        refresh();
      },
      onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
    });
  const addItem = useSend('items', () => setItemForm(blankItem), 'inv.itemAdded');
  const receive = useSend('stock/receipts', () => setReceiveForm(blankReceive), 'inv.received');
  const issue = useSend('stock/issues', () => setIssueForm(blankIssue), 'inv.issued');
  const adjust = useSend('stock/adjustments', () => setAdjustForm(blankAdjust), 'inv.adjusted');

  const check = (parsed: Check, extra: Errors = {}) => {
    const next: Errors = { ...extra };
    if (!parsed.success)
      for (const i of parsed.error?.issues ?? []) next[String(i.path[0])] = i.message;
    setErrors(next);
    return parsed.success && Object.keys(next).length === 0;
  };
  const minor = (text: string) => parseMajor(text, currency);
  const itemOptions = (
    <>
      <option value="">{t('inv.choose')}</option>
      {allItems.data?.map((i) => (
        <option key={i.id} value={i.id}>
          {i.name} ({i.sku})
        </option>
      ))}
    </>
  );

  return (
    <section aria-label={t('inv.tab.stock')} className="space-y-6">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <div className="flex flex-wrap items-end gap-4">
        <TextField
          className="min-w-64 flex-1"
          label={t('inv.search')}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={lowOnly}
            onChange={(e) => {
              setLowOnly(e.target.checked);
              setPage(1);
            }}
          />
          {t('inv.lowOnly')}
        </label>
      </div>
      {items.data?.data.length === 0 && <p className="text-slate-600">{t('inv.noItems')}</p>}
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
        {items.data?.data.map((i) => (
          <li key={i.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
            <span>
              <span className="font-medium">{i.name}</span>{' '}
              <span className="font-mono text-xs">{i.sku}</span>
              {i.category ? (
                <span className="ml-2 rounded bg-slate-100 px-2 py-0.5 text-xs">{i.category}</span>
              ) : null}
            </span>
            <span className={i.low ? 'font-medium text-red-700' : ''}>
              {t('inv.onHand', { n: i.onHand, unit: i.unit })} ·{' '}
              {formatMinor(i.valueMinor, currency)}
              {i.low ? ` · ${t('inv.low')}` : ''}
            </span>
          </li>
        ))}
      </ul>
      {items.data && items.data.meta.totalPages > 1 && (
        <Pager page={page} totalPages={items.data.meta.totalPages} onPage={setPage} />
      )}

      {can('inventory.manage', schoolId) && (
        <form
          noValidate
          aria-label={t('inv.addItem')}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            const parsed = createItemSchema.safeParse({
              sku: itemForm.sku,
              name: itemForm.name,
              unit: itemForm.unit || undefined,
              category: itemForm.category,
              reorderLevel: itemForm.reorder === '' ? 0 : Number(itemForm.reorder),
            });
            if (check(parsed) && parsed.success) {
              setMessage(null);
              addItem.mutate(parsed.data);
            }
          }}
        >
          <TextField
            label={t('inv.sku')}
            value={itemForm.sku}
            error={errors.sku}
            onChange={(e) => setItemForm({ ...itemForm, sku: e.target.value })}
          />
          <TextField
            label={t('inv.itemName')}
            value={itemForm.name}
            error={errors.name}
            onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
          />
          <TextField
            label={t('inv.unit')}
            value={itemForm.unit}
            error={errors.unit}
            onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}
          />
          <TextField
            label={t('inv.category')}
            value={itemForm.category}
            error={errors.category}
            onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
          />
          <TextField
            label={t('inv.reorder')}
            inputMode="numeric"
            value={itemForm.reorder}
            error={errors.reorderLevel}
            onChange={(e) => setItemForm({ ...itemForm, reorder: e.target.value })}
          />
          <div className="flex items-end">
            <button type="submit" className={primaryButton} disabled={addItem.isPending}>
              {t('inv.addItem')}
            </button>
          </div>
        </form>
      )}

      {can('inventory.receive', schoolId) && (
        <form
          noValidate
          aria-label={t('inv.receive')}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            const cost = minor(receiveForm.cost);
            const parsed = receiveStockSchema.safeParse({
              itemId: receiveForm.itemId,
              supplierId: receiveForm.supplierId,
              quantity: Number(receiveForm.quantity),
              unitCostMinor: cost,
              reference: receiveForm.reference,
            });
            if (
              check(parsed, cost === null ? { unitCostMinor: t('inv.invalid') } : {}) &&
              parsed.success
            ) {
              setMessage(null);
              receive.mutate(parsed.data);
            }
          }}
        >
          <div>
            <label htmlFor="recv-item" className="block text-sm font-medium">
              {t('inv.item')}
            </label>
            <select
              id="recv-item"
              className={selectClass}
              value={receiveForm.itemId}
              onChange={(e) => setReceiveForm({ ...receiveForm, itemId: e.target.value })}
            >
              {itemOptions}
            </select>
            {errors.itemId && (
              <p role="alert" className="mt-1 text-xs text-red-700">
                {t('inv.chooseOne')}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="recv-supplier" className="block text-sm font-medium">
              {t('inv.supplier')}
            </label>
            <select
              id="recv-supplier"
              className={selectClass}
              value={receiveForm.supplierId}
              onChange={(e) => setReceiveForm({ ...receiveForm, supplierId: e.target.value })}
            >
              <option value="">{t('inv.choose')}</option>
              {suppliers.data?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {errors.supplierId && (
              <p role="alert" className="mt-1 text-xs text-red-700">
                {t('inv.chooseOne')}
              </p>
            )}
          </div>
          <TextField
            label={t('inv.quantity')}
            inputMode="numeric"
            value={receiveForm.quantity}
            error={errors.quantity}
            onChange={(e) => setReceiveForm({ ...receiveForm, quantity: e.target.value })}
          />
          <TextField
            label={t('inv.unitCost', { currency })}
            inputMode="decimal"
            value={receiveForm.cost}
            error={errors.unitCostMinor}
            onChange={(e) => setReceiveForm({ ...receiveForm, cost: e.target.value })}
          />
          <TextField
            label={t('inv.reference')}
            value={receiveForm.reference}
            error={errors.reference}
            onChange={(e) => setReceiveForm({ ...receiveForm, reference: e.target.value })}
          />
          <div className="flex items-end">
            <button type="submit" className={primaryButton} disabled={receive.isPending}>
              {t('inv.receive')}
            </button>
          </div>
        </form>
      )}

      {can('inventory.issue', schoolId) && (
        <form
          noValidate
          aria-label={t('inv.issue')}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            const parsed = issueStockSchema.safeParse({
              itemId: issueForm.itemId,
              quantity: Number(issueForm.quantity),
              issuedTo: issueForm.issuedTo,
            });
            if (check(parsed) && parsed.success) {
              setMessage(null);
              issue.mutate(parsed.data);
            }
          }}
        >
          <div>
            <label htmlFor="issue-item" className="block text-sm font-medium">
              {t('inv.issueItem')}
            </label>
            <select
              id="issue-item"
              className={selectClass}
              value={issueForm.itemId}
              onChange={(e) => setIssueForm({ ...issueForm, itemId: e.target.value })}
            >
              {itemOptions}
            </select>
            {errors.itemId && (
              <p role="alert" className="mt-1 text-xs text-red-700">
                {t('inv.chooseOne')}
              </p>
            )}
          </div>
          <TextField
            label={t('inv.issueQuantity')}
            inputMode="numeric"
            value={issueForm.quantity}
            error={errors.quantity}
            onChange={(e) => setIssueForm({ ...issueForm, quantity: e.target.value })}
          />
          <TextField
            label={t('inv.issuedTo')}
            value={issueForm.issuedTo}
            error={errors.issuedTo}
            onChange={(e) => setIssueForm({ ...issueForm, issuedTo: e.target.value })}
          />
          <div className="flex items-end">
            <button type="submit" className={primaryButton} disabled={issue.isPending}>
              {t('inv.issue')}
            </button>
          </div>
        </form>
      )}

      {can('inventory.adjust', schoolId) && (
        <form
          noValidate
          aria-label={t('inv.adjust')}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            const cost = adjustForm.cost.trim() === '' ? undefined : minor(adjustForm.cost);
            const parsed = adjustStockSchema.safeParse({
              itemId: adjustForm.itemId,
              quantity: Number(adjustForm.quantity),
              unitCostMinor: cost === null ? undefined : cost,
              note: adjustForm.note,
            });
            if (
              check(parsed, cost === null ? { unitCostMinor: t('inv.invalid') } : {}) &&
              parsed.success
            ) {
              setMessage(null);
              adjust.mutate(parsed.data);
            }
          }}
        >
          <div>
            <label htmlFor="adj-item" className="block text-sm font-medium">
              {t('inv.adjustItem')}
            </label>
            <select
              id="adj-item"
              className={selectClass}
              value={adjustForm.itemId}
              onChange={(e) => setAdjustForm({ ...adjustForm, itemId: e.target.value })}
            >
              {itemOptions}
            </select>
            {errors.itemId && (
              <p role="alert" className="mt-1 text-xs text-red-700">
                {t('inv.chooseOne')}
              </p>
            )}
          </div>
          <TextField
            label={t('inv.change')}
            hint={t('inv.changeHint')}
            inputMode="numeric"
            value={adjustForm.quantity}
            error={errors.quantity}
            onChange={(e) => setAdjustForm({ ...adjustForm, quantity: e.target.value })}
          />
          <TextField
            label={t('inv.gainCost', { currency })}
            inputMode="decimal"
            value={adjustForm.cost}
            error={errors.unitCostMinor}
            onChange={(e) => setAdjustForm({ ...adjustForm, cost: e.target.value })}
          />
          <TextField
            label={t('inv.reason')}
            value={adjustForm.note}
            error={errors.note}
            onChange={(e) => setAdjustForm({ ...adjustForm, note: e.target.value })}
          />
          <div className="sm:col-span-4">
            <button type="submit" className={primaryButton} disabled={adjust.isPending}>
              {t('inv.adjust')}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        <h2 className="text-lg font-semibold">{t('inv.recent')}</h2>
        {movements.data?.data.length === 0 && (
          <p className="text-slate-600">{t('inv.noMovements')}</p>
        )}
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
          {movements.data?.data.map((m) => (
            <li key={m.id} className="flex flex-wrap justify-between gap-2 p-3">
              <span>
                {m.occurredOn} · {t(`inv.kind.${m.kind}` as MessageKey)} · {m.itemName} ·{' '}
                {m.quantity > 0 ? `+${m.quantity}` : m.quantity}
                {m.supplierName ? ` · ${m.supplierName}` : ''}
                {m.issuedTo ? ` → ${m.issuedTo}` : ''}
              </span>
              <span>{formatMinor(m.valueMinor, currency)}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
